/** XeAgents.ai**/
import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { AuthStorage } from "./auth-storage";

// CSRF token management for defense-in-depth security
const CSRF_COOKIE_NAME = "XSRF-TOKEN";
const CSRF_HEADER_NAME = "X-XSRF-TOKEN";

/**
 * Get CSRF token from cookie
 * The server sets this cookie and we include it in state-changing requests
 */
function getCsrfToken(): string | null {
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === CSRF_COOKIE_NAME) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

// Custom error class that preserves HTTP status and response data
export class ApiError extends Error {
  status: number;
  data: any;
  conflictType?: string;
  connectedAgentName?: string;
  campaignName?: string;
  campaignStatus?: string;
  requiresAction?: boolean;
  actionUrl?: string;
  helpText?: string;
  error?: string;
  
  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    
    // Extract conflict-specific fields for easier access
    if (data) {
      this.conflictType = data.conflictType;
      this.connectedAgentName = data.connectedAgentName;
      this.campaignName = data.campaignName;
      this.campaignStatus = data.campaignStatus;
      this.requiresAction = data.requiresAction;
      this.actionUrl = data.actionUrl;
      this.helpText = data.helpText;
      this.error = data.error;
    }
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = await res.text();
    let message = text || res.statusText;
    let data: any = null;
    
    // Try to parse JSON error response and extract the error message
    try {
      data = JSON.parse(text);
      message = data.error || data.message || text;
    } catch {
      // Not JSON, use raw text
    }
    
    // Throw ApiError with status and full response data
    throw new ApiError(message, res.status, data);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: Record<string, string> = {};

  if (data) {
    headers["Content-Type"] = "application/json";
  }

  const authHeader = AuthStorage.getAuthHeader();
  if (authHeader) {
    headers["Authorization"] = authHeader;
  }

  // Add CSRF token for state-changing requests (defense-in-depth)
  // Even though Bearer tokens are inherently protected, we add CSRF for extra security
  const stateChangingMethods = ["POST", "PUT", "PATCH", "DELETE"];
  if (stateChangingMethods.includes(method.toUpperCase())) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers[CSRF_HEADER_NAME] = csrfToken;
    }
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const headers: Record<string, string> = {};
    
    const authHeader = AuthStorage.getAuthHeader();
    if (authHeader) {
      headers["Authorization"] = authHeader;
    }

    const res = await fetch(queryKey.join("/") as string, {
      headers,
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
